import { supabase } from '../lib/supabase';

export interface UserWithMetrics {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'manager' | 'user';
  status: 'active' | 'inactive' | 'suspended';
  createdAt: Date;
  lastActivity: Date;
  monthlyInteractions: number;
}

export interface TenantStats {
  currentUsers: number;
  maxUsers: number;
  plan: 'free' | 'premium';
  canAddUsers: boolean;
}

class UserManagementService {
  /**
   * Buscar todos os usuários do tenant com métricas
   */
  async getTenantUsers(tenantId: string): Promise<UserWithMetrics[]> {
    try {
      console.log('📊 Buscando usuários do tenant:', tenantId);

      // Buscar usuários
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      if (!users) return [];

      // Buscar total de mensagens de cada usuário (mês atual)
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const usersWithMetrics = await Promise.all(
        users.map(async (user) => {
          console.log(`📊 Buscando métricas para ${user.email}...`);
          
          // Buscar total de mensagens do mês atual
          const { count: messagesCount, error: messagesError } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .gte('created_at', startOfMonth.toISOString());

          if (messagesError) {
            console.warn(`⚠️ Erro ao buscar mensagens de ${user.email}:`, messagesError);
          } else {
            console.log(`📧 ${user.email}: ${messagesCount || 0} mensagens`);
          }

          // Buscar total de mensagens de assistentes do mês atual
          const { count: assistantMessagesCount, error: assistantError } = await supabase
            .from('assistant_messages')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .gte('created_at', startOfMonth.toISOString());

          if (assistantError) {
            console.warn(`⚠️ Erro ao buscar mensagens de assistente de ${user.email}:`, assistantError);
          } else {
            console.log(`🤖 ${user.email}: ${assistantMessagesCount || 0} mensagens de assistente`);
          }

          // Buscar última mensagem para determinar última atividade
          const { data: lastMessage, error: lastMsgError } = await supabase
            .from('messages')
            .select('created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (lastMsgError && lastMsgError.code !== 'PGRST116') { // PGRST116 = no rows
            console.warn(`⚠️ Erro ao buscar última mensagem de ${user.email}:`, lastMsgError);
          }

          const { data: lastAssistantMessage, error: lastAstError } = await supabase
            .from('assistant_messages')
            .select('created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (lastAstError && lastAstError.code !== 'PGRST116') {
            console.warn(`⚠️ Erro ao buscar última mensagem de assistente de ${user.email}:`, lastAstError);
          }

          // Determinar última atividade
          let lastActivityDate = new Date(user.created_at);
          
          if (lastMessage) {
            const messageDate = new Date(lastMessage.created_at);
            if (messageDate > lastActivityDate) {
              lastActivityDate = messageDate;
            }
          }
          
          if (lastAssistantMessage) {
            const assistantMessageDate = new Date(lastAssistantMessage.created_at);
            if (assistantMessageDate > lastActivityDate) {
              lastActivityDate = assistantMessageDate;
            }
          }

          const totalInteractions = (messagesCount || 0) + (assistantMessagesCount || 0);

          console.log(`✅ ${user.email}: ${totalInteractions} interações, última atividade: ${lastActivityDate.toLocaleString()}`);

          return {
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role as 'owner' | 'manager' | 'user',
            status: user.status as 'active' | 'inactive' | 'suspended',
            createdAt: new Date(user.created_at),
            lastActivity: lastActivityDate,
            monthlyInteractions: totalInteractions
          };
        })
      );

      return usersWithMetrics;
    } catch (error) {
      console.error('❌ Erro ao buscar usuários:', error);
      throw error;
    }
  }

  /**
   * Buscar estatísticas do tenant
   */
  async getTenantStats(tenantId: string): Promise<TenantStats> {
    try {
      // Buscar tenant
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('plan, max_users')
        .eq('id', tenantId)
        .single();

      if (tenantError) throw tenantError;

      // Contar usuários ativos
      const { count, error: countError } = await supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('status', 'active');

      if (countError) throw countError;

      const currentUsers = count || 0;
      const maxUsers = tenant.max_users || 1;

      return {
        currentUsers,
        maxUsers,
        plan: tenant.plan as 'free' | 'premium',
        canAddUsers: currentUsers < maxUsers
      };
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas:', error);
      throw error;
    }
  }

  /**
   * Convidar/Criar novo usuário
   */
  async inviteUser(
    tenantId: string,
    email: string,
    name: string,
    role: 'user' | 'manager' = 'user',
    password: string
  ): Promise<{ success: boolean; userId?: string; error?: string }> {
    try {
      console.log('📨 Convidando usuário:', email);

      // Verificar limite de usuários
      const stats = await this.getTenantStats(tenantId);
      if (!stats.canAddUsers) {
        return {
          success: false,
          error: 'Limite de usuários atingido. Faça upgrade do plano.'
        };
      }

      // Verificar se email já existe
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .single();

      if (existingUser) {
        return {
          success: false,
          error: 'Email já cadastrado no sistema.'
        };
      }

      // Criar usuário no Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            tenant_id: tenantId
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Usuário não criado');

      // Aguardar trigger criar o usuário na tabela users
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Atualizar role do usuário (trigger cria como 'user' por padrão)
      if (role === 'manager') {
        const { error: updateError } = await supabase
          .from('users')
          .update({ role: 'manager' })
          .eq('id', authData.user.id);

        if (updateError) {
          console.error('⚠️ Erro ao atualizar role:', updateError);
        }
      }

      // Criar métrica inicial
      const currentMonth = new Date();
      currentMonth.setDate(1);
      const monthStr = currentMonth.toISOString().split('T')[0];

      await supabase
        .from('user_monthly_metrics')
        .insert({
          user_id: authData.user.id,
          tenant_id: tenantId,
          month: monthStr
        });

      console.log('✅ Usuário criado com sucesso:', authData.user.id);

      return {
        success: true,
        userId: authData.user.id
      };
    } catch (error) {
      console.error('❌ Erro ao convidar usuário:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar usuário'
      };
    }
  }

  /**
   * Atualizar role do usuário
   */
  async updateUserRole(
    userId: string,
    newRole: 'user' | 'manager'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔄 Atualizando role do usuário:', userId, 'para', newRole);

      const { error } = await supabase
        .from('users')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      console.log('✅ Role atualizada com sucesso');
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao atualizar role:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar role'
      };
    }
  }

  /**
   * Desativar usuário (não remove, apenas desativa)
   */
  async deactivateUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚫 Desativando usuário:', userId);

      const { error } = await supabase
        .from('users')
        .update({ status: 'inactive' })
        .eq('id', userId);

      if (error) throw error;

      console.log('✅ Usuário desativado com sucesso');
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao desativar usuário:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao desativar usuário'
      };
    }
  }

  /**
   * Reativar usuário
   */
  async reactivateUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('✅ Reativando usuário:', userId);

      const { error } = await supabase
        .from('users')
        .update({ status: 'active' })
        .eq('id', userId);

      if (error) throw error;

      console.log('✅ Usuário reativado com sucesso');
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao reativar usuário:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao reativar usuário'
      };
    }
  }

  /**
   * Remover usuário permanentemente (apenas para owners)
   */
  async deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🗑️ Removendo usuário:', userId);

      // Verificar se não é owner
      const { data: user, error: checkError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      if (checkError) throw checkError;

      if (user.role === 'owner') {
        return {
          success: false,
          error: 'Não é possível remover o proprietário da conta'
        };
      }

      // Remover usuário
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      console.log('✅ Usuário removido com sucesso');
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao remover usuário:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao remover usuário'
      };
    }
  }

  /**
   * Criar usuário diretamente (sem convite)
   */
  async createUserDirectly(
    tenantId: string,
    email: string,
    name: string,
    password: string,
    role: 'user' | 'manager' = 'user'
  ): Promise<{ success: boolean; error?: string; userId?: string }> {
    try {
      console.log('👤 Criando usuário direto:', { email, name, role, tenantId });

      // 1. Obter token de autenticação atual
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return {
          success: false,
          error: 'Sessão não encontrada'
        };
      }

      // 2. Chamar Edge Function simplificada
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      console.log('🚀 Chamando Edge Function create-user-simple...');

      const response = await fetch(`${supabaseUrl}/functions/v1/create-user-simple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          email,
          password,
          name,
          role,
          tenantId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro HTTP:', response.status, errorText);
        return {
          success: false,
          error: `Erro ${response.status}: ${errorText}`
        };
      }

      const result = await response.json();
      console.log('📥 Resposta:', result);

      if (!result.success) {
        console.error('❌ Erro na criação:', result.error);
        return {
          success: false,
          error: result.error || 'Erro ao criar usuário'
        };
      }

      console.log('✅ Usuário criado:', result.userId);
      return {
        success: true,
        userId: result.userId
      };
    } catch (error) {
      console.error('❌ Erro exception:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar usuário'
      };
    }
  }

  /**
   * Obtém o link permanente de convite do tenant
   */
  async getTenantInviteLink(tenantId: string): Promise<{ success: boolean; link?: string; token?: string; error?: string }> {
    try {
      console.log('🔗 Buscando link de convite do tenant:', tenantId);

      const { data: tenant, error } = await supabase
        .from('tenants')
        .select('invite_token, invite_link_enabled, name')
        .eq('id', tenantId)
        .single();

      if (error || !tenant) {
        console.error('❌ Erro ao buscar tenant:', error);
        return {
          success: false,
          error: 'Tenant não encontrado'
        };
      }

      // Se não tem token, gerar um novo
      if (!tenant.invite_token) {
        const { data: result, error: genError } = await supabase
          .rpc('generate_tenant_invite_token', { tenant_id_param: tenantId });

        if (genError) {
          console.error('❌ Erro ao gerar token:', genError);
          return {
            success: false,
            error: 'Erro ao gerar link de convite'
          };
        }

        const token = result;
        const link = `${window.location.origin}/signup?invite=${token}`;
        
        return {
          success: true,
          link,
          token
        };
      }

      const link = `${window.location.origin}/signup?invite=${tenant.invite_token}`;
      
      console.log('✅ Link gerado:', link);

      return {
        success: true,
        link,
        token: tenant.invite_token
      };
    } catch (error) {
      console.error('❌ Erro ao obter link de convite:', error);
      return {
        success: false,
        error: 'Erro ao obter link de convite'
      };
    }
  }

  /**
   * Regenera o token de convite do tenant (invalida o link antigo)
   */
  async regenerateTenantInviteLink(tenantId: string): Promise<{ success: boolean; link?: string; error?: string }> {
    try {
      console.log('🔄 Regenerando link de convite do tenant:', tenantId);

      const { data: newToken, error } = await supabase
        .rpc('generate_tenant_invite_token', { tenant_id_param: tenantId });

      if (error) {
        console.error('❌ Erro ao regenerar token:', error);
        return {
          success: false,
          error: 'Erro ao regenerar link'
        };
      }

      const link = `${window.location.origin}/signup?invite=${newToken}`;
      
      console.log('✅ Novo link gerado:', link);

      return {
        success: true,
        link
      };
    } catch (error) {
      console.error('❌ Erro ao regenerar link:', error);
      return {
        success: false,
        error: 'Erro ao regenerar link'
      };
    }
  }

  /**
   * Valida o token permanente de invite do tenant
   */
  async validateTenantInviteToken(token: string): Promise<{ success: boolean; tenant?: { id: string; name: string }; error?: string }> {
    try {
      console.log('🔍 Validando token de invite:', token);

      const { data, error } = await supabase
        .rpc('get_tenant_by_invite_token', { token_param: token });

      if (error || !data || data.length === 0) {
        console.error('❌ Erro ao validar token:', error);
        return {
          success: false,
          error: 'Link de convite inválido ou expirado'
        };
      }

      const tenantData = Array.isArray(data) ? data[0] : data;

      console.log('✅ Token válido para tenant:', tenantData);

      return {
        success: true,
        tenant: {
          id: tenantData.id,
          name: tenantData.name
        }
      };
    } catch (error) {
      console.error('❌ Erro ao validar token:', error);
      return {
        success: false,
        error: 'Erro ao validar link de convite'
      };
    }
  }

  /**
   * Cria uma conta usando o token permanente de invite
   */
  async createAccountWithInviteToken(
    token: string,
    tenantId: string,
    name: string,
    email: string,
    password: string
  ): Promise<{ success: boolean; user?: any; error?: string }> {
    try {
      console.log('🎯 Criando conta com invite token:', { email, name, tenantId });

      // 1. Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            skip_tenant_creation: true
          }
        }
      });

      if (authError) {
        console.error('❌ Erro ao criar usuário no Auth:', authError);
        return {
          success: false,
          error: `Erro ao criar conta: ${authError.message}`
        };
      }

      if (!authData.user) {
        return {
          success: false,
          error: 'Erro ao criar usuário'
        };
      }

      console.log('✅ Usuário criado no Auth:', authData.user.id);

      // 2. Aguardar trigger criar tenant (que será deletado)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 3. Deletar tenant auto-criado pelo trigger
      const { error: deleteTenantError } = await supabase
        .from('tenants')
        .delete()
        .eq('id', authData.user.id);

      if (deleteTenantError) {
        console.warn('⚠️ Erro ao deletar tenant auto-criado:', deleteTenantError);
      }

      // 4. Atualizar o usuário na tabela customizada com o tenant correto
      const { error: updateUserError } = await supabase
        .from('users')
        .update({ tenant_id: tenantId })
        .eq('id', authData.user.id);

      if (updateUserError) {
        console.error('❌ Erro ao atualizar tenant do usuário:', updateUserError);
        return {
          success: false,
          error: 'Erro ao vincular usuário ao tenant'
        };
      }

      // 5. Buscar dados completos do usuário
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userError || !userData) {
        console.error('❌ Erro ao buscar usuário:', userError);
        return {
          success: false,
          error: 'Erro ao buscar dados do usuário'
        };
      }

      console.log('✅ Usuário criado e vinculado com sucesso!');

      return {
        success: true,
        user: {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          tenantId: userData.tenant_id,
          role: userData.role,
          status: userData.status,
          createdAt: userData.created_at
        }
      };
    } catch (error) {
      console.error('❌ Erro ao criar conta:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar conta'
      };
    }
  }

  /**
   * Gerar link de cadastro (token válido por 7 dias) - DEPRECATED
   */
  async generateSignupLink(
    tenantId: string,
    role: 'user' | 'manager' = 'user'
  ): Promise<{ success: boolean; error?: string; link?: string; token?: string }> {
    try {
      console.log('🔗 Gerando link de cadastro para tenant:', tenantId);

      // 1. Criar token único
      const token = `${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}${Date.now()}`;
      
      // 2. Salvar na tabela signup_tokens
      const { error: insertError } = await supabase
        .from('signup_tokens')
        .insert({
          tenant_id: tenantId,
          token,
          role,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 dias
          status: 'active'
        });

      if (insertError) throw insertError;

      // 3. Gerar link
      const link = `${window.location.origin}/signup?token=${token}`;

      console.log('✅ Link gerado com sucesso');
      return {
        success: true,
        link,
        token
      };
    } catch (error) {
      console.error('❌ Erro ao gerar link:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao gerar link'
      };
    }
  }

  /**
   * Enviar convite por email para novo usuário (DEPRECATED - usar createUserDirectly ou generateSignupLink)
   */
  async sendUserInvitation(
    tenantId: string,
    invitedBy: string,
    email: string,
    name: string,
    role: 'user' | 'manager' = 'user'
  ): Promise<{ success: boolean; error?: string; invitationId?: string }> {
    try {
      console.log('📧 Enviando convite para:', email);

      // 1. Verificar se o email já está em uso
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .single();

      if (existingUser) {
        return {
          success: false,
          error: 'Este email já está cadastrado na plataforma'
        };
      }

      // 2. Verificar se já existe um convite pendente para este email
      const { data: existingInvitation } = await supabase
        .from('user_invitations')
        .select('id, status, expires_at')
        .eq('tenant_id', tenantId)
        .eq('email', email)
        .eq('status', 'pending')
        .single();

      if (existingInvitation && new Date(existingInvitation.expires_at) > new Date()) {
        return {
          success: false,
          error: 'Já existe um convite pendente para este email'
        };
      }

      // 3. Cancelar convites antigos
      if (existingInvitation) {
        await supabase
          .from('user_invitations')
          .update({ status: 'cancelled' })
          .eq('id', existingInvitation.id);
      }

      // 4. Criar novo convite
      const { data: invitation, error: invitationError } = await supabase
        .from('user_invitations')
        .insert({
          tenant_id: tenantId,
          invited_by: invitedBy,
          email,
          name,
          role,
          status: 'pending',
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 horas
        })
        .select()
        .single();

      if (invitationError) throw invitationError;

      // 5. Buscar dados do tenant e inviter para o email
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('name')
        .eq('id', tenantId)
        .single();

      const { data: inviterData } = await supabase
        .from('users')
        .select('name')
        .eq('id', invitedBy)
        .single();

      // 6. Enviar email via Edge Function
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        
        const emailResponse = await fetch(
          `${supabaseUrl}/functions/v1/send-invitation-email`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: invitation.email,
              name: invitation.name,
              inviterName: inviterData?.name || 'Administrador',
              companyName: tenantData?.name || 'Space AI',
              role: invitation.role,
              token: invitation.token,
              expiresAt: invitation.expires_at
            })
          }
        );

        if (!emailResponse.ok) {
          console.warn('⚠️ Erro ao enviar email, mas convite foi criado:', await emailResponse.text());
        } else {
          console.log('✅ Email enviado com sucesso');
        }
      } catch (emailError) {
        console.warn('⚠️ Erro ao enviar email, mas convite foi criado:', emailError);
      }

      console.log('✅ Convite criado:', invitation.id);

      return {
        success: true,
        invitationId: invitation.id
      };
    } catch (error) {
      console.error('❌ Erro ao enviar convite:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao enviar convite'
      };
    }
  }

  /**
   * Reenviar convite expirado
   */
  async resendInvitation(invitationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔄 Reenviando convite:', invitationId);

      // Buscar convite
      const { data: invitation, error: fetchError } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (fetchError || !invitation) {
        return {
          success: false,
          error: 'Convite não encontrado'
        };
      }

      // Atualizar convite
      const { error: updateError } = await supabase
        .from('user_invitations')
        .update({
          status: 'pending',
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          sent_at: new Date().toISOString(),
          token: `${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // TODO: Reenviar email
      console.log('✅ Convite reenviado');

      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao reenviar convite:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao reenviar convite'
      };
    }
  }

  /**
   * Buscar convites pendentes do tenant
   */
  async getPendingInvitations(tenantId: string) {
    try {
      const { data, error } = await supabase
        .from('user_invitations')
        .select(`
          *,
          invited_by_user:users!user_invitations_invited_by_fkey(name, email)
        `)
        .eq('tenant_id', tenantId)
        .in('status', ['pending', 'expired'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Expirar convites automaticamente
      const now = new Date();
      const invitationsToExpire = data.filter(
        inv => inv.status === 'pending' && new Date(inv.expires_at) < now
      );

      if (invitationsToExpire.length > 0) {
        await Promise.all(
          invitationsToExpire.map(inv =>
            supabase
              .from('user_invitations')
              .update({ status: 'expired' })
              .eq('id', inv.id)
          )
        );
      }

      return data || [];
    } catch (error) {
      console.error('❌ Erro ao buscar convites:', error);
      return [];
    }
  }

  /**
   * Validar signup token
   */
  async validateSignupToken(
    token: string
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log('🔍 Validando signup token:', token);

      // Buscar token
      const { data, error } = await supabase
        .from('signup_tokens')
        .select('tenant_id, role, status, expires_at')
        .eq('token', token)
        .eq('status', 'active')
        .single();

      if (error || !data) {
        return {
          success: false,
          error: 'Token inválido ou expirado'
        };
      }

      // Verificar expiração
      if (new Date(data.expires_at) < new Date()) {
        return {
          success: false,
          error: 'Token expirado'
        };
      }

      console.log('✅ Token válido para tenant:', data.tenant_id);
      return {
        success: true,
        data: {
          tenantId: data.tenant_id,
          role: data.role
        }
      };
    } catch (error) {
      console.error('❌ Erro ao validar token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao validar token'
      };
    }
  }

  /**
   * Criar conta usando signup token
   */
  async createAccountWithSignupToken(
    token: string,
    name: string,
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string; user?: any }> {
    try {
      console.log('📝 Criando conta com signup token para:', email);

      // 1. Validar token
      const validation = await this.validateSignupToken(token);
      if (!validation.success || !validation.data) {
        return {
          success: false,
          error: validation.error || 'Token inválido'
        };
      }

      const { tenantId, role } = validation.data;

      // 2. Verificar se o email já está em uso
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .single();

      if (existingUser) {
        return {
          success: false,
          error: 'Este email já está cadastrado'
        };
      }

      // 3. Criar usuário no Supabase Auth (não cria sessão pois é via signup público)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            full_name: name,
            skip_tenant_creation: true
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Erro ao criar usuário no Auth');

      console.log('✅ Usuário criado no Auth:', authData.user.id);

      // 4. Aguardar trigger processar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 5. Deletar o tenant criado automaticamente pelo trigger (se existir)
      const { data: autoTenant } = await supabase
        .from('tenants')
        .select('id')
        .eq('domain', `${email}.internal`)
        .single();

      if (autoTenant) {
        console.log('🗑️ Deletando tenant automático criado pelo trigger');
        await supabase
          .from('tenants')
          .delete()
          .eq('id', autoTenant.id);
      }

      // 6. Atualizar usuário na tabela customizada com o tenant correto
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name,
          tenant_id: tenantId,
          role: role || 'user',
          status: 'active'
        })
        .eq('id', authData.user.id);

      if (updateError) {
        console.error('❌ Erro ao atualizar usuário:', updateError);
        throw new Error('Erro ao vincular usuário ao tenant');
      }

      // 7. Buscar usuário atualizado
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userError || !newUser) {
        throw new Error('Erro ao buscar usuário criado');
      }

      console.log('✅ Usuário vinculado ao tenant:', newUser.tenant_id);

      // 8. Incrementar contador de usos do token
      await supabase.rpc('use_signup_token', { p_token: token });

      console.log('🎉 Conta criada com sucesso via signup token!');

      return {
        success: true,
        user: {
          id: newUser.id,
          name: newUser.name,
          email: newUser.email,
          tenantId: newUser.tenant_id,
          role: newUser.role,
          status: newUser.status
        }
      };
    } catch (error) {
      console.error('❌ Erro ao criar conta:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar conta'
      };
    }
  }
}

export const userManagementService = new UserManagementService();

